import { Box, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { InfoCircle, Instagram, Star1 } from "iconsax-react";
import { useTranslation } from "react-i18next";

import { CreateShopStepHeading, FloatingTextField } from "@/components/Molecules";
import type { CreateShopSocialsStepProps } from "./CreateShopSocialsStep.types";
import * as styles from "./CreateShopSocialsStep.styles";

export function CreateShopSocialsStep({ socialRows, anySocialFilled }: CreateShopSocialsStepProps) {
  const { t } = useTranslation(["shops", "settings"]);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <>
      <CreateShopStepHeading
        titleKey="create.socials.title"
        subtitleKey="create.socials.subtitle"
        mobileSubtitleKey="create.socials.mobileSubtitle"
      />

      <Box sx={styles.banner}>
        <Star1
          size={isDesktop ? 19 : 16}
          variant="Bold"
          color={theme.palette.secondary.main}
        />
        <Typography sx={styles.helper}>
          {t(isDesktop ? "settings:socials.banner" : "settings:socials.mobileBanner")}
        </Typography>
      </Box>

      <Box sx={styles.stack}>
        {socialRows.map((row) => {
          const Icon = row.icon;

          return (
            <Box key={row.key} sx={styles.row(row.isPrimary)}>
              <Box sx={styles.icon(row.colorToken)}>
                <Icon size={isDesktop ? 20 : 17} variant="Bold" />
              </Box>

              <Box sx={styles.field}>
                <FloatingTextField
                  label={t(`settings:${row.labelKey}`)}
                  dir="ltr"
                  placeholder={t(`settings:${row.placeholderKey}`)}
                  value={row.value}
                  onChange={(event) => row.onChange(event.target.value)}
                />
                <Typography sx={styles.helper}>{t(`settings:${row.helperKey}`)}</Typography>
              </Box>

              <IconButton
                size="small"
                onClick={row.onSetPrimary}
                sx={styles.star(row.isFilled, row.isPrimary)}
              >
                <Star1
                  size={isDesktop ? 20 : 18}
                  variant={row.isPrimary ? "Bold" : "Linear"}
                  color={
                    row.isPrimary ? theme.palette.secondary.main : theme.palette.text.secondary
                  }
                />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      <Box sx={styles.botHint}>
        <Box sx={styles.botHintIcon}>
          <Instagram size={isDesktop ? 18 : 16} variant="Bold" color="currentColor" />
        </Box>
        <Typography sx={styles.helper}>{t("create.socials.instagramBot")}</Typography>
      </Box>

      {!anySocialFilled ? (
        <Box sx={styles.hint}>
          <InfoCircle
            size={isDesktop ? 16 : 15}
            variant="Bold"
            color={theme.palette.text.secondary}
          />
          {t(isDesktop ? "create.socials.hint" : "create.socials.mobileHint")}
        </Box>
      ) : null}
    </>
  );
}
